import { useState, type FormEvent } from 'react';
import { AppIcon } from '../app/AppIcon.js';
import { AnimatePresence, motion } from 'motion/react';
import { CoreconfMark } from '../brand/CoreconfMark.js';
import { ApiError, auth, type SessionUser } from '../api/client.js';
import { MfaChallenge } from './MfaChallenge.js';
import { RecoveryFlow } from './RecoveryFlow.js';

/**
 * Sign in and registration.
 *
 * The first screen carries the product's claim about itself, so it says
 * something true and specific rather than something reassuring and vague. The
 * three points below the form are the actual architecture — private messages
 * really are end-to-end encrypted and server channels really are not — because
 * a privacy claim a user later discovers to be decorative costs more trust than
 * making no claim at all.
 */

const EASE_SWIM = [0.16, 1, 0.3, 1] as const;
const MIN_PASSWORD = 12;

type Mode = 'login' | 'register';

/** A sign-in that stopped halfway, waiting on a second factor. */
interface PendingMfa {
  challenge: string;
  expiresInMs: number;
}

export function AuthScreen({ onAuthenticated }: { onAuthenticated: (user: SessionUser) => void }) {
  const [mode, setMode] = useState<Mode>('login');
  const [pendingMfa, setPendingMfa] = useState<PendingMfa | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const usernameProblem =
    username !== '' && !/^[a-z0-9._-]{2,32}$/.test(username.toLowerCase())
      ? 'Sadece küçük harf, rakam, nokta, tire ve alt çizgi.'
      : null;

  const passwordProblem =
    mode === 'register' && password !== '' && password.length < MIN_PASSWORD
      ? `En az ${MIN_PASSWORD} karakter.`
      : null;

  const canSubmit =
    username.length >= 2 &&
    password.length > 0 &&
    usernameProblem === null &&
    passwordProblem === null &&
    !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError(null);
    try {
      if (mode === 'register') {
        const result = await auth.register(username, password);
        onAuthenticated(result.user);
        return;
      }

      const result = await auth.login(username, password);
      // The discriminant, not a truthiness check on `user`: an account with a
      // second factor gets a ticket and no user at all, and treating that as a
      // failed login would strand everyone who enabled 2FA.
      if (result.mfaRequired === true) {
        setPendingMfa({ challenge: result.challenge, expiresInMs: result.expiresInMs });
        return;
      }
      onAuthenticated(result.user);
    } catch (caught) {
      // Branch on the stable code, never on message text.
      if (caught instanceof ApiError) {
        setError(
          caught.code === 'RATE_LIMITED'
            ? `Çok fazla deneme. ${caught.retryAfter ?? 60} saniye sonra tekrar dene.`
            : caught.message,
        );
      } else {
        setError('Beklenmeyen bir hata oldu.');
      }
    } finally {
      setBusy(false);
      setPassword('');
    }
  }

  if (pendingMfa !== null) {
    return (
      <main id="main-content" tabIndex={-1} className="auth-page">
        <MfaChallenge
          challenge={pendingMfa.challenge}
          expiresInMs={pendingMfa.expiresInMs}
          onAuthenticated={onAuthenticated}
          onCancel={() => setPendingMfa(null)}
        />
      </main>
    );
  }

  if (recovering) {
    return (
      <main id="main-content" tabIndex={-1} className="auth-page">
        <RecoveryFlow onBack={() => setRecovering(false)} />
      </main>
    );
  }

  return (
    <main id="main-content" tabIndex={-1} className="auth-page">
      <motion.div
        className="auth-card auth-card--access"
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: EASE_SWIM }}
      >
        <section className="auth-story" aria-label="Coreconf hakkında">
          <div className="auth-brand">
            <CoreconfMark size={72} />
            <span className="auth-brand-kicker">Gizlilik öncelikli iletişim</span>
            <h1>coreconf</h1>
            <p>Topluluğunla konuş, ekranını paylaş; özel konuşmaların sende kalsın.</p>
          </div>

          <ul className="auth-claims">
            <li>
              <AppIcon name="shield" size={14} hole="transparent" style={{ color: 'var(--tone-visible)' }} />
              <span>
                <strong>Sunucu kanalları şifresiz saklanır.</strong> Moderasyon için gerekli — ve
                bunu gizlemiyoruz.
              </span>
            </li>
            <li>
              <AppIcon name="lock" size={14} hole="transparent" style={{ color: 'var(--tone-encrypted)' }} />
              <span>
                <strong>Özel mesajlar uçtan uca şifreli.</strong> MLS (RFC 9420); anahtarlar
                cihazında kalır, sunucu içeriği okuyamaz. Kiminle konuştuğunu doğrulamak için
                güvenlik numarasını karşılaştır.
              </span>
            </li>
            <li>
              <AppIcon name="key" size={14} hole="transparent" style={{ color: 'var(--ink-faint)' }} />
              <span>
                Parolan <strong>Argon2id</strong> ile saklanır, oturum çerezini JavaScript okuyamaz.
              </span>
            </li>
          </ul>

          <a className="auth-download" href="/download/Aksolot-setup.exe">
            <AppIcon name="download" size={14} hole="transparent" />
            Windows uygulaması <span>0.2 beta · 95 MB · kurulum</span>
          </a>
        </section>

        <section className="auth-access" aria-labelledby="auth-access-title">
          <div className="auth-access-head">
            <span>Güvenli alanına dön</span>
            <h2 id="auth-access-title">{mode === 'login' ? 'Tekrar hoş geldin' : 'Yeni alanını oluştur'}</h2>
            <p>
              {mode === 'login'
                ? 'Kaldığın yerden devam etmek için hesabına giriş yap.'
                : 'Bir kullanıcı adı ve güçlü bir parola ile hemen başla.'}
            </p>
          </div>

          <div className="auth-tabs" role="tablist">
          {(['login', 'register'] as const).map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={mode === tab}
              className="auth-tab"
              data-active={mode === tab}
              onClick={() => {
                setMode(tab);
                setError(null);
              }}
            >
              {mode === tab ? (
                <motion.span
                  layoutId="auth-tab-indicator"
                  className="auth-tab-indicator"
                  transition={{ duration: 0.4, ease: EASE_SWIM }}
                />
              ) : null}
              <span style={{ position: 'relative' }}>
                {tab === 'login' ? 'Giriş yap' : 'Hesap oluştur'}
              </span>
            </button>
          ))}
          </div>

          <form onSubmit={submit} noValidate>
          <label className="field">
            <span className="field-label">Kullanıcı adı</span>
            <input
              className="field-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={usernameProblem !== null}
              aria-describedby={usernameProblem !== null ? 'username-problem' : undefined}
              placeholder="aksolotl"
            />
            {/* Errors sit next to the field they describe, not in a summary at
                the top where the user has to work out which one is wrong. */}
            {usernameProblem !== null ? (
              <span className="field-problem" id="username-problem">
                {usernameProblem}
              </span>
            ) : null}
          </label>

          <label className="field">
            <span className="field-label">Parola</span>
            <div className="field-affix">
              <input
                className="field-input"
                type={revealed ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                aria-invalid={passwordProblem !== null}
                aria-describedby={passwordProblem !== null ? 'password-problem' : undefined}
                placeholder={mode === 'register' ? `En az ${MIN_PASSWORD} karakter` : '••••••••••••'}
              />
              <button
                type="button"
                className="icon-btn"
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? 'Parolayı gizle' : 'Parolayı göster'}
              >
                {revealed ? <AppIcon name="eye" size={17} hole="transparent" slashed /> : <AppIcon name="eye" size={17} hole="transparent" />}
              </button>
            </div>
            {passwordProblem !== null ? (
              <span className="field-problem" id="password-problem">
                {passwordProblem}
              </span>
            ) : null}
          </label>

          <AnimatePresence>
            {error !== null ? (
              <motion.div
                className="auth-error"
                role="alert"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: EASE_SWIM }}
              >
                <AppIcon name="warning" size={15} hole="transparent" />
                {error}
              </motion.div>
            ) : null}
          </AnimatePresence>

          <button type="submit" className="auth-submit" disabled={!canSubmit}>
            {busy ? 'Bir saniye…' : mode === 'login' ? 'Giriş yap' : 'Hesabı oluştur'}
            <span className="send-well">
              <AppIcon name="arrowRight" size={14} hole="transparent" />
            </span>
          </button>

          {/* Login only. On the registration tab there is nothing to recover. */}
          {mode === 'login' ? (
            <button
              type="button"
              className="auth-secondary"
              onClick={() => {
                setRecovering(true);
                setError(null);
              }}
            >
              Şifremi unuttum
            </button>
          ) : null}
          </form>
        </section>
      </motion.div>
    </main>
  );
}
